import {range} from '@benchling/js-utils';
import produce from 'immer';
import reactUpdate, {Spec} from 'immutability-helper';
import {UpdateSpecPath} from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {InitialCellValue} from 'notebook/structured-tables/util/getInitialCellValueFromLiveValue';
import {
  EvaluatedTable,
  FullTableMap,
  OutputTableMap,
  RelativeCellAddress,
} from 'notebook/tables/excess/Evaluator';
import {
  createDeleteTokenUpdater,
  createInsertTokenUpdater,
  createRenameTableTokenUpdater,
  createReorderTokenUpdater,
  createSortRowsTokenUpdater,
  TokenUpdater,
} from 'notebook/tables/excess/ops/TokenUpdater';
import tokenizer, {Token} from 'notebook/tables/excess/Tokenizer';
import * as util from 'notebook/tables/excess/Util';
import {
  BaseTableOpDiffWithOptionalFormulaShifts,
  CELL_FORMAT_PROPERTIES,
  COLUMN_FIELD_DEFAULTS,
  Cell,
  CellIndex,
  Cells,
  InsertableColumnInfo,
  MoveColumnsTableOp,
  MoveRowsTableOp,
  MultidocShiftFormulasTableOpDiffMap,
  MultidocTableOpDiffMap,
  MultiSelectionBox,
  RowFields,
  ShiftFormulasTableOpDiffMap,
  SingleDocTableOpFunction,
  TABLE_ROW,
  Table,
  TableDimension,
  TableOpDiff,
  TableOpDiffMap,
  TableOpFunction,
  ToggleSingleInputTableOp,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {omit} from 'util/omitKey';
import pick from 'util/pick';
import reorderSubsequence from 'util/reorderSubsequence';
import {ArrayElement} from 'util/Types';

export interface SingleDocTableOpOutput {
  tables: FullTableMap;
  box: MultiSelectionBox | null;
  tableOp?: TableOpDiffMap;
}

export interface TableOpOutput {
  table: Table;
  tokenUpdaters: Array<TokenUpdater>;
  box: MultiSelectionBox | null;
  dim: TableDimension | null;
  tableOp?: TableOpDiffMap;
}

/**
 * Shifts cell text by the given token updater if the cell text is a formula.
 *
 * @param text           The text of the cell, whether it has a formula or not
 * @param tokenUpdater   Token updater function
 * @param cellPosition   The position of the cell containing the token
 * @returns              New text of the cell, or null if the cell should not be updated
 */
function shiftCellText(
  text: string,
  tokenUpdater: TokenUpdater,
  cellPosition: RelativeCellAddress
): string | null {
  // Skip cells that are not formulas
  const formulaText = util.getFormulaText(text);
  if (formulaText == null) {
    return null;
  }

  let tokens: Array<Token>;
  try {
    tokens = tokenizer.tokenize(formulaText);
  } catch (e) {
    // Skip cells that have an invalid formula
    if (e instanceof util.FormulaTokenizeError) {
      return null;
    }
    throw e;
  }

  // Reconstruct the formula with the token updates
  const updatedFormulaText = tokens.map((token) => tokenUpdater(token, cellPosition) ?? token.text).join('');
  return updatedFormulaText === formulaText ? null : `=${updatedFormulaText}`;
}

/**
 * Shift references (over all cells in `tables`) using the provided token updater.
 *
 * @param tables        Map from table name to table. Treated as immutable.
 * @param tokenUpdater  Token updater function
 * @param dim           One of [TABLE_ROW, TABLE_COLUMN]
 * @return              New map from table name to table
 */
export function shiftFormulas(
  tables: FullTableMap,
  tokenUpdater: TokenUpdater,
  documentName: string | null,
  dim?: TableDimension
): {tables: FullTableMap; tableOp?: ShiftFormulasTableOpDiffMap} {
  let tableOp: ShiftFormulasTableOpDiffMap | undefined = {};
  tables = _.mapObject(tables, (currentTable, currentTableName) => {
    const tableUpdates: UpdateSpecPath = {};
    const cellUpdates: {[index: number]: {[index: number]: UpdateSpecPath}} = {};
    for (let i = 0; i < currentTable.table.length; i++) {
      const row = currentTable.table[i];
      for (let j = 0; j < row.length; j++) {
        // If there are updates to the formula, register the change as a table update
        const cell = row[j];
        const newText = shiftCellText(cell.text, tokenUpdater, {
          row: i,
          column: j,
          table: currentTableName,
          document: documentName,
        });
        if (newText != null) {
          // and then register it as a change to the table
          cellUpdates[i] ??= {};
          cellUpdates[i][j] = {text: {$set: newText}};
          // and mark it as an updated cell in the TableOpCoords
          if (!tableOp!.hasOwnProperty(currentTable.name)) {
            tableOp![currentTable.name] = {type: 'shift-formulas', shiftedCells: [{row: i, column: j}]};
          } else {
            tableOp![currentTable.name].shiftedCells.push({row: i, column: j});
          }
        }
      }
    }
    if (!_.isEmpty(cellUpdates)) {
      tableUpdates.table = cellUpdates;
    }

    // For DataTables, we also need to shift the associated config columns' defaultValues as these contain
    // row-restricted formulas.
    for (let j = 0; j < currentTable.columnConstraints.length; j++) {
      const columnConstraint = currentTable.columnConstraints[j];
      if (columnConstraint?.structuredTableColumn?.defaultValue != null) {
        const newText = shiftCellText(
          columnConstraint.structuredTableColumn.defaultValue.text,
          tokenUpdater,
          {row: 0, column: j, table: currentTableName} // row=0 because a default value assumes it's in the top row
        );
        if (newText != null) {
          tableUpdates.columnConstraints ??= {};
          // @ts-expect-error
          tableUpdates.columnConstraints[j] = {
            structuredTableColumn: {defaultValue: {text: {$set: newText}}},
          };
        }
      }
    }

    // If there are updates registered for the table, apply them; otherwise, return the unchanged table.
    if (!_.isEmpty(tableUpdates)) {
      return reactUpdate(currentTable, tableUpdates);
    } else {
      return currentTable;
    }
  });
  if (Object.keys(tableOp).length === 0) {
    tableOp = undefined;
  }

  return {
    tables,
    tableOp,
  };
}

/**
 * Shift all fields in `table` that depend on the number of rows.
 */
function shiftRowFields(table: Table, op: 'insert' | 'delete', numRows: number, atIndex: number): Table {
  const numColumns = table.table[0].length;
  // If someone adds another RowField, this type will remind them to add a default value here.
  const rowFieldDefaults: Required<{
    [fieldName in keyof RowFields]: ArrayElement<RowFields[fieldName]>;
  }> = {
    table: new Array(numColumns).fill({text: ''}),
    rowData: {},
    rowLabels: '',
  };

  const fieldNamesPresent = Object.keys(rowFieldDefaults).filter(
    (fieldName) => table[fieldName as keyof RowFields] != null
  ) as Array<keyof RowFields>;

  const updates: UpdateSpecPath = {};
  for (const fieldName of fieldNamesPresent) {
    if (op === 'insert') {
      updates[fieldName] = {
        $splice: [[atIndex, 0, ...Array(numRows).fill(rowFieldDefaults[fieldName])]],
      };
    } else if (op === 'delete') {
      updates[fieldName] = {$splice: [[atIndex, numRows]]};
    }
  }

  return reactUpdate(table, updates);
}

/**
 * Reorder all fields in `table` that depend on row or column order.
 */
function reorderFields(
  table: Table,
  newToOldIndices: Array<number>,
  dim: TableDimension
): {table: Table; tableOp: TableOpDiff} {
  let fieldNames: Array<keyof RowFields | keyof typeof COLUMN_FIELD_DEFAULTS> = [];
  if (dim === TableDimension.ROW) {
    fieldNames = Object.keys({
      table: null,
      rowData: null,
      rowLabels: null,
      // This is an ugly Typescript hack, but this ensures that if someone adds another RowField,
      // this type will remind them to include it here.
    } satisfies Required<{
      [fieldName in keyof RowFields]: unknown;
    }>) as Array<keyof RowFields>;
  } else {
    fieldNames = Object.keys(COLUMN_FIELD_DEFAULTS) as Array<keyof typeof COLUMN_FIELD_DEFAULTS>;
  }

  const updates: UpdateSpecPath = {};
  for (const fieldName of fieldNames) {
    const field = table[fieldName];
    if (field != null) {
      updates[fieldName] = {
        $set: newToOldIndices.map((newIndex) => field[newIndex]),
      };
    }
  }

  if (dim === TableDimension.COLUMN) {
    // Also reorder the cells in each row
    const newTable = table.table.map((row) => newToOldIndices.map((newIndex) => row[newIndex]));
    updates.table = {
      $set: newTable,
    };
  }

  const tableOp: MoveRowsTableOp | MoveColumnsTableOp =
    dim === TableDimension.ROW
      ? {
          type: 'move-rows',
          rows: newToOldIndices
            .map((from, to) => (from !== to ? {from, to} : null))
            .filter((move) => move != null),
        }
      : {
          type: 'move-cols',
          cols: newToOldIndices
            .map((from, to) => (from !== to ? {from, to} : null))
            .filter((move) => move != null),
        };

  return {table: reactUpdate(table, updates), tableOp};
}

/**
 * Same as deleteRows but allows for non-contiguous row deletion to be performed in a single operation.
 *
 * @param table
 * @param documentName
 * @param ranges - Array of [start, end] pairs (both inclusive) representing the rows to be deleted
 */
export function deleteRowsNonContiguous(
  table: Table,
  documentName: string | null,
  ranges: Array<[number, number]>
): TableOpOutput {
  const numRows = table.table.length;
  // sort the ranges in desc order, so we don't have to worry about shifting indices
  ranges = ranges.sort(([start1], [start2]) => start2 - start1);
  const minIdxInRanges = ranges[ranges.length - 1][0];
  const maxIdxInRanges = ranges[0][1];
  assert(
    0 <= minIdxInRanges && minIdxInRanges <= maxIdxInRanges && maxIdxInRanges <= numRows - 1,
    `cannot delete rows outside of table. [${minIdxInRanges}, ${maxIdxInRanges}] outside of [0, ${
      numRows - 1
    }]`
  );
  assert(
    ranges.every((range, idx) => idx === 0 || ranges[idx - 1][0] > range[1]),
    'ranges to delete cannot overlap'
  );

  let [minI, maxI] = ranges[0];
  const tokenUpdaters: Array<TokenUpdater> = [];
  let newTable = table;
  let newTokenUpdaters: Array<TokenUpdater> = [];

  for (const [start, end] of ranges) {
    minI = Math.min(minI, start);
    maxI = Math.max(maxI, end);
    ({table: newTable, tokenUpdaters: newTokenUpdaters} = deleteRows(newTable, documentName, start, end));
    tokenUpdaters.push(...newTokenUpdaters);
  }

  const tableOp: TableOpDiffMap = {
    [table.name]: {
      type: 'delete-rows',
      rowIndexes: ranges.flatMap((r) => range(r[0], r[1])).toSorted(),
    },
  };

  return {
    table: newTable,
    tokenUpdaters,
    dim: TableDimension.ROW,
    box: {
      minI: Math.min(minI, newTable.table.length - 1),
      maxI: Math.min(maxI, newTable.table.length - 1),
      minJ: 0,
      maxJ: newTable.table[0].length - 1,
    },
    tableOp,
  };
}

/**
 * @param table
 * @param documentName
 * @param minI             The index of the first row to be deleted
 * @param maxI             The index of the last row to be deleted
 * @return tableOpOutput - The token updaters have not been ran yet, and must be executed by the
 * caller.
 *    box: the region that was previously selected. If part of the previously selected
 *         region is no longer available, the selection will shrink appropriately.
 */
export function deleteRows(
  table: Table,
  documentName: string | null,
  minI: number,
  maxI: number
): TableOpOutput {
  const numRows = table.table.length;
  assert(
    0 <= minI && minI <= maxI && maxI <= numRows - 1,
    `cannot delete rows outside of table [${minI}, ${maxI}] outside of [0, ${numRows - 1}]`
  );
  assert(
    table.allowNoRows || !(minI === 0 && maxI === numRows - 1),
    `cannot delete all rows [${minI}, ${maxI}]`
  );

  const tokenUpdater = createDeleteTokenUpdater(table, documentName, TableDimension.ROW, minI, maxI);
  table = shiftRowFields(table, 'delete', maxI - minI + 1, minI);

  const tableOp: TableOpDiffMap = {
    [table.name]: {
      type: 'delete-rows',
      rowIndexes: range(minI, maxI),
    },
  };

  return {
    table,
    tokenUpdaters: [tokenUpdater],
    dim: TableDimension.ROW,
    box: {
      minI: Math.min(minI, table.table.length - 1),
      maxI: Math.min(maxI, table.table.length - 1),
      minJ: 0,
      maxJ: table.table.length > 0 ? table.table[0].length - 1 : 0,
    },
    tableOp,
  };
}

/**
 * Converts a multi-doc operation to be ran in the old-style as a single doc operation.
 * This is a temporary shim to allow us to migrate operations one at a time.
 * @param op
 * @returns
 */
export function wrapTableOpForSingleDoc(op: TableOpFunction): SingleDocTableOpFunction {
  return (originalTableMap: FullTableMap, computedTables: OutputTableMap, targetTableName: string) => {
    assert(originalTableMap[targetTableName] !== undefined, `No table with name ${targetTableName}`);
    const {
      table: newTable,
      tokenUpdaters,
      box,
      dim,
      tableOp,
    } = op(originalTableMap[targetTableName], null, computedTables[targetTableName], originalTableMap);

    // Create new table map with the modified table
    let newTables: FullTableMap = {...originalTableMap, [targetTableName]: newTable};
    let shiftTableOp: ShiftFormulasTableOpDiffMap = {};

    // Formula shift tables in the target doc (local references only)
    for (const tokenUpdater of tokenUpdaters) {
      const {tables, tableOp: tokenUpdaterTableOp} = shiftFormulas(
        newTables,
        tokenUpdater,
        null,
        dim ?? undefined
      );
      newTables = tables;
      shiftTableOp = mergeShiftFormulaTableOpDiffs(shiftTableOp, tokenUpdaterTableOp);
    }

    return {
      tables: newTables,
      box,
      ...(tableOp != null ? {tableOp: mergeFormulaShiftOpsIntoTableOpDiffMap(tableOp, shiftTableOp)} : {}),
    };
  };
}

/**
 * @param tables           Map from table name to table
 * @param targetTableName  The table from which cells will be deleted
 * @param minJ             The index of the first column to be deleted
 * @param maxJ             The index of the last column to be deleted
 * @return tableOpOutput - The token updaters have not been ran yet, and must be executed by the
 * caller.
 *    box: the region that was previously selected. If part of the previously selected
 *         region is no longer available, the selection will shrink appropriately.
 */
export function deleteColumns(
  table: Table,
  documentName: string | null,
  minJ: number,
  maxJ: number
): TableOpOutput {
  const numColumns = table.table[0].length;
  assert(
    0 <= minJ && minJ <= maxJ && maxJ <= numColumns - 1,
    `cannot delete columns outside of table [${minJ}, ${maxJ}] outside of [0, ${numColumns - 1}]`
  );
  assert(!(minJ === 0 && maxJ === numColumns - 1), `cannot delete all columns [${minJ}, ${maxJ}]`);

  // Create paramterized token updater
  const tokenUpdater = createDeleteTokenUpdater(table, documentName, TableDimension.COLUMN, minJ, maxJ);

  // Delete columns from the target table
  const spliceOp: Spec<Array<Cell>> = {$splice: [[minJ, maxJ - minJ + 1]]};
  const newTable: Table = reactUpdate(
    table,
    // @ts-expect-error
    _.chain(COLUMN_FIELD_DEFAULTS)
      .mapObject(() => spliceOp)
      .extend({
        table: {
          $apply(table: Array<Array<Cell>>): Array<Array<Cell>> {
            return table.map((row) => reactUpdate(row, spliceOp));
          },
        },
      })
      .value()
  );

  const tableOp: TableOpDiffMap = {
    [table.name]: {
      type: 'delete-cols',
      colIndexes: range(minJ, maxJ),
    },
  };

  // Return table and token updater
  return {
    table: newTable,
    tokenUpdaters: [tokenUpdater],
    dim: TableDimension.COLUMN,
    box: {
      minI: 0,
      maxI: newTable.table.length - 1,
      minJ: Math.min(minJ, newTable.table[0].length - 1),
      maxJ: Math.min(maxJ, newTable.table[0].length - 1),
    },
    tableOp,
  };
}

/**
 * @param table
 * @param documentName
 * @param i                The index of the row above which the rows will be inserted.
 * @param numNewRows       The number of new rows that will be inserted.
 * @return                 New table and token updater
 */
export function insertRowsHelper(
  table: Table,
  documentName: string | null,
  i: number,
  numNewRows: number
): TableOpOutput {
  const numRows = table.table.length;
  assert(0 <= i && i <= numRows, `unknown row index ${i}, outside of possible range [0, ${numRows}]`);
  assert(numNewRows >= 1, `expected a positive number of new rows. specified number was ${{numNewRows}}`);
  const tokenUpdater = createInsertTokenUpdater(table, documentName, TABLE_ROW, i, numNewRows);
  const newTable = shiftRowFields(table, 'insert', numNewRows, i);
  const numColumns = table.table[0].length;

  const tableOp: TableOpDiffMap = {
    [table.name]: {type: 'insert-rows', rowIndexes: range(i, i + numNewRows - 1)},
  };

  return {
    table: newTable,
    tokenUpdaters: [tokenUpdater],
    dim: TableDimension.ROW,
    box: {
      minI: i,
      maxI: i + numNewRows - 1,
      minJ: 0,
      maxJ: numColumns - 1,
    },
    tableOp,
  };
}

/**
 * @param table
 * @param documentName
 * @param minI             The index of the first row selected
 * @param maxI             The index of the last row selected
 * @return tableOpOutput - The token updaters have not been ran yet, and must be executed by the
 * caller.
 */
export function insertRowsAbove(
  table: Table,
  documentName: string | null,
  minI: number,
  maxI: number
): TableOpOutput {
  const numRows = table.table.length;
  assert(
    0 <= minI && minI <= maxI && maxI <= numRows - 1,
    `rows selected were [${minI}, ${maxI}] outside of [0, ${numRows - 1}]`
  );
  const numNewRows = maxI - minI + 1;
  return insertRowsHelper(table, documentName, minI, numNewRows);
}

/**
 * @param table
 * @param documentName
 * @param minI             The index of the first row selected
 * @param maxI             The index of the last row selected
 * @return tableOpOutput - The token updaters have not been ran yet, and must be executed by the
 * caller.
 */
export function insertRowsBelow(
  table: Table,
  documentName: string | null,
  minI: number,
  maxI: number
): TableOpOutput {
  const numRows = table.table.length;
  assert(
    0 <= minI && minI <= maxI && maxI <= numRows - 1,
    `rows selected were [${minI}, ${maxI}] outside of [0, ${numRows - 1}]`
  );
  const numNewRows = maxI - minI + 1;
  return insertRowsHelper(table, documentName, maxI + 1, numNewRows);
}

/**
 * @package Private helper, performs validations and structural changes only, no formula shifting
 */
export function insertColumnsHelper(
  table: Table,
  j: number,
  numNewColumns: number,
  columnFields?: Partial<InsertableColumnInfo>,
  initialCellValues?: Array<InitialCellValue>
): Table {
  if (columnFields == null) {
    columnFields = {};
  }

  // If a table has default values (must be a structured table) then you cannot add multiple columns a once
  if (initialCellValues) {
    assert(numNewColumns === 1, 'Expected structured tables to only insert 1 column at a time');
  }

  assert(table.table.length > 0, 'Cannot insert columns into an empty table');
  const numColumns = table.table[0].length;
  assert(
    0 <= j && j <= numColumns,
    `cannot insert columns at index ${j}, outside of valid range [0, ${numColumns}]`
  );
  assert(
    numNewColumns >= 1,
    `expected a positive number of new columns. specified number was ${numNewColumns}`
  );
  if (initialCellValues) {
    const numRows = table.table.length;
    assert(initialCellValues.length === numRows, 'Expected one initial cell value per row');
  }

  // oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-function-return-type
  const spliceOp = (value: any) => ({$splice: [[j, 0, value]]});

  let newTable = table;
  // eslint-disable-next-line no-restricted-syntax
  _.times(numNewColumns, () => {
    newTable = reactUpdate(
      newTable,
      // @ts-expect-error
      _.chain(COLUMN_FIELD_DEFAULTS)
        .mapObject((defaultValue, field) =>
          // @ts-expect-error
          spliceOp(columnFields[field] != null ? columnFields[field] : defaultValue)
        )
        .extend({
          table: {
            $apply(table: Array<Array<Cell>>): Array<Array<Cell>> {
              return table.map((row: Array<Cell>, i: number) =>
                // TS gets confused by the reactUpdate types, so just use any for now.
                // oxlint-disable-next-line @typescript-eslint/no-explicit-any
                reactUpdate(row, spliceOp(initialCellValues?.[i] ?? {text: ''}) as any)
              );
            },
          },
        })
        .value()
    );
  });

  return newTable;
}

/**
 * Inserts columns to the left of the selected columns.
 *
 * @param table
 * @param documentName
 * @param minJ               The index of the first column selected
 * @param maxJ               The index of the last column selected
 * @param columnInfo         The column info for the new columns
 * @param initialCellValues  The initial values for cells in the new columns
 * @return tableOpOutput - The token updaters have not been ran yet, and must be executed by the
 * caller.
 */
export function insertColumnsLeft(
  table: Table,
  documentName: string | null,
  minJ: number,
  maxJ: number,
  columnInfo: Partial<InsertableColumnInfo> = {},
  initialCellValues?: Array<InitialCellValue>
): TableOpOutput {
  const numColumns = table.table[0].length;
  assert(
    0 <= minJ && minJ <= maxJ && maxJ <= numColumns - 1,
    `columns selected were [${minJ}, ${maxJ}] outside of [0, ${numColumns - 1}]`
  );
  const numRows = table.table.length;
  const numNewColumns = maxJ - minJ + 1;
  // set the width to the specified width or the width of the column to the right
  const newColumnWidth = columnInfo.widths ?? table.widths[minJ];
  // Whether or not we are inserting single input column(s).
  const areSingleInput = columnInfo.columnConstraints?.structuredTableColumn?.isSingleInput ?? false;

  const tokenUpdater = createInsertTokenUpdater(
    table,
    documentName,
    TableDimension.COLUMN,
    minJ,
    numNewColumns
  );

  const modifiedTable = insertColumnsHelper(
    table,
    minJ,
    numNewColumns,
    {
      ...columnInfo,
      widths: newColumnWidth,
    },
    initialCellValues
  );

  const box = areSingleInput
    ? {
        minI: 0,
        maxI: 0,
        minJ,
        maxJ,
      }
    : {
        minI: 0,
        maxI: numRows - 1,
        minJ,
        maxJ,
      };

  const tableOp: TableOpDiffMap = {
    [table.name]: {type: 'insert-cols', colIndexes: range(minJ, minJ + numNewColumns - 1)},
  };

  return {
    table: modifiedTable,
    tokenUpdaters: [tokenUpdater],
    dim: TableDimension.COLUMN,
    box,
    tableOp,
  };
}

/**
 * Inserts columns to the right of the selected columns.
 *
 * @param table
 * @param documentName
 * @param minJ               The index of the first column selected
 * @param maxJ               The index of the last column selected
 * @param columnInfo         The column info for the new columns
 * @param initialCellValues  The initial values for cells in the new columns
 * @return tableOpOutput - The token updaters have not been ran yet, and must be executed by the
 * caller.
 */
export function insertColumnsRight(
  table: Table,
  documentName: string | null,
  minJ: number,
  maxJ: number,
  columnInfo: Partial<InsertableColumnInfo> = {},
  initialCellValues?: Array<InitialCellValue>
): TableOpOutput {
  const numColumns = table.table[0].length;
  assert(
    0 <= minJ && minJ <= maxJ && maxJ <= numColumns - 1,
    `columns selected were [${minJ}, ${maxJ}] outside of [0, ${numColumns - 1}]`
  );

  const numRows = table.table.length;
  const numNewColumns = maxJ - minJ + 1;
  // set the width to the specified width or the width of the column to the left
  const newColumnWidth = columnInfo.widths ?? table.widths[maxJ];
  // Whether or not we are inserting single input column(s).
  const areSingleInput = columnInfo.columnConstraints?.structuredTableColumn?.isSingleInput ?? false;

  const tokenUpdater = createInsertTokenUpdater(
    table,
    documentName,
    TableDimension.COLUMN,
    maxJ + 1,
    numNewColumns
  );

  const modifiedTable = insertColumnsHelper(
    table,
    maxJ + 1,
    numNewColumns,
    {
      ...columnInfo,
      widths: newColumnWidth,
    },
    initialCellValues
  );

  const box = areSingleInput
    ? {
        minI: 0,
        maxI: 0,
        minJ: maxJ + 1,
        maxJ: maxJ + numNewColumns,
      }
    : {
        minI: 0,
        maxI: numRows - 1,
        minJ: maxJ + 1,
        maxJ: maxJ + numNewColumns,
      };

  const tableOp: TableOpDiffMap = {
    [table.name]: {
      type: 'insert-cols',
      colIndexes: range(maxJ + 1, maxJ + numNewColumns),
    },
  };

  return {
    table: modifiedTable,
    tokenUpdaters: [tokenUpdater],
    dim: TableDimension.COLUMN,
    box,
    tableOp,
  };
}

interface ReorderSortOptions {
  /**
   * Mapping from old row index to new row index.
   */
  newToOldIndices: Array<number>;
}

export interface ReorderManualOptions {
  /**
   * First index of sequence of elements to move, inclusive.
   */
  startIndex: number;
  /**
   * Last index of sequence of elements to move, inclusive.
   */
  endIndex: number;
  /**
   * New index of first element of sequence in original order index-space.
   */
  insertPosition: number;
  /**
   * The dimension (row or column) along which to reorder.
   */
  dim: TableDimension;
}

export type ReorderOptions = ReorderSortOptions | ReorderManualOptions;

function isReorderOptionsForSort(options: ReorderOptions): options is ReorderSortOptions {
  return options.hasOwnProperty('newToOldIndices');
}

/**
 * Reorders rows or columns in a table and updates table formulas to reflect reordering.
 * Updates cell references differently depending on whether the reordering is a sort or a manual reordering.
 *
 * @param  table
 * @param  documentName
 * @param  options          Options describing how to reorder the rows or columns
 * @return tableOpOutput - The token updaters have not been ran yet, and must be executed by the
 * caller.
 */
export function reorderRowsOrColumns(
  table: Table,
  documentName: string | null,
  options: ReorderOptions
): TableOpOutput {
  const dim = isReorderOptionsForSort(options) ? TableDimension.ROW : options.dim;
  const dimensionSize = dim === TableDimension.ROW ? table.table.length : table.table[0].length;

  let tokenUpdater: TokenUpdater;
  let newToOldIndices: Array<number>;

  if (isReorderOptionsForSort(options)) {
    // Sorting, so validate the newToOldIndices array
    newToOldIndices = options.newToOldIndices;
    assert(
      newToOldIndices.length === dimensionSize,
      'Expected newToOldIndices to have the same length as the number of rows'
    );
    assert(
      new Set(Object.values(newToOldIndices)).size === dimensionSize,
      'Cannot move multiple rows to the same row position'
    );
    assert(
      Object.values(newToOldIndices).every((newIndex) => 0 <= newIndex && newIndex < dimensionSize),
      `Expected every index to be in range [0, ${dimensionSize - 1}]`
    );

    tokenUpdater = createSortRowsTokenUpdater(table, documentName, dim, newToOldIndices);
  } else {
    // Manual reordering, so validate the arguments
    const {startIndex, endIndex, insertPosition} = options;
    assert(
      0 <= startIndex && startIndex <= endIndex && endIndex < dimensionSize,
      'Expected startIndex and endIndex to be in range'
    );
    assert(0 <= insertPosition && insertPosition <= dimensionSize, 'Expected insertIndex to be in range');

    newToOldIndices = reorderSubsequence(
      Array.from(Array(dimensionSize), (_, i) => i), // Array of indices [0, ..., dimensionSize - 1]
      {
        insertPosition,
        startIndex,
        endIndex: endIndex + 1, // + 1 because endIndex is inclusive but argument should be exclusive
      }
    );

    tokenUpdater = createReorderTokenUpdater(table, documentName, dim, startIndex, endIndex, newToOldIndices);
  }
  const {table: newTable, tableOp} = reorderFields(table, newToOldIndices, dim);

  return {
    table: newTable,
    tokenUpdaters: [tokenUpdater],
    dim,
    box: null, // Don't change the selection
    tableOp: {[table.name]: tableOp},
  };
}

export function mergeIntoSingleInput(
  table: Table,
  documentName: string | null,
  evaluatedTable: EvaluatedTable,
  startIndex: number,
  endIndex: number
): TableOpOutput {
  return mergeOrUnmergeSingleInput(table, documentName, evaluatedTable, startIndex, endIndex, true);
}

export function unmergeSingleInput(
  table: Table,
  documentName: string | null,
  evaluatedTable: EvaluatedTable,
  startIndex: number,
  endIndex: number
): TableOpOutput {
  return mergeOrUnmergeSingleInput(table, documentName, evaluatedTable, startIndex, endIndex, false);
}

function mergeOrUnmergeSingleInput(
  table: Table,
  documentName: string | null,
  evaluatedTable: EvaluatedTable,
  startIndex: number,
  endIndex: number,
  toSingleInput: boolean
): TableOpOutput {
  const numRows = table.table.length;
  const singleInputBoundaryIndex = TableUtil.findSingleInputBoundaryIndex(table);

  // merge or unmerge each column
  let tableWithMergeChanges: Table = table;
  const cellUpdates: Array<CellIndex> = [];
  for (let columnIndex = startIndex; columnIndex <= endIndex; columnIndex++) {
    const selection: MultiSelectionBox = {
      minI: 0,
      maxI: numRows - 1,
      minJ: columnIndex,
      maxJ: columnIndex,
    };
    const mergeResult = toSingleInput
      ? mergeRegion(tableWithMergeChanges, selection)
      : unmergeRegion(tableWithMergeChanges, evaluatedTable, selection);
    tableWithMergeChanges = mergeResult.table;

    // Collect cell updates from the merge/unmerge operation
    const tableOp = mergeResult.tableOp?.[table.name];
    if (tableOp) {
      assert(tableOp.type === 'update-cells', 'Expected merging to produce update-cells table op');
      cellUpdates.push(...tableOp.cells);
    }
  }

  // set isSingleInput accordingly on each column
  // (we do this in a separate loop to reduce immer updates)
  tableWithMergeChanges = produce(tableWithMergeChanges, (draft) => {
    for (let columnIndex = startIndex; columnIndex <= endIndex; columnIndex++) {
      const columnConstraint = draft.columnConstraints[columnIndex];
      // Single Inputs are a Structured Table-only feature
      if (columnConstraint?.structuredTableColumn) {
        columnConstraint.structuredTableColumn.isSingleInput = toSingleInput;
      }
    }
  });

  return moveBetweenSingleInputsAndColumns(
    tableWithMergeChanges,
    documentName,
    singleInputBoundaryIndex,
    startIndex,
    endIndex,
    cellUpdates
  );
}

/**
 * Shifts cell column indexes from their original positions to their new positions using move records.
 *
 * @param cells      Array of cell indexes in original column positions
 * @param cols       Move records where {from, to} means column at old index 'from' moved to new index 'to'
 * @param numColumns Total number of columns (needed to handle columns that didn't move)
 * @returns          Array of cell indexes with shifted column positions
 */
function shiftCellIndexesForColumnMoves(
  cells: Array<CellIndex>,
  cols: Array<{from: number; to: number}>,
  numColumns: number
): Array<CellIndex> {
  // Build old-to-new mapping from move records (unmoved columns stay at same index)
  const oldToNew = Array.from({length: numColumns}, (_, i) => i);
  for (const {from, to} of cols) {
    oldToNew[from] = to;
  }

  return cells.map((cell) => ({
    row: cell.row,
    column: oldToNew[cell.column],
  }));
}

function moveBetweenSingleInputsAndColumns(
  table: Table,
  documentName: string | null,
  singleInputBoundaryIndex: number,
  startIndex: number,
  endIndex: number,
  cellUpdates: Array<CellIndex> = []
): TableOpOutput {
  const reorderResult = reorderRowsOrColumns(table, documentName, {
    startIndex,
    endIndex,
    insertPosition: singleInputBoundaryIndex,
    dim: TableDimension.COLUMN,
  });

  assert(reorderResult.tableOp !== undefined, 'Expected reorder to produce table op');
  const moveColsOp = reorderResult.tableOp[table.name] as MoveColumnsTableOp;

  // Shift cell indexes to their final column positions using the move records
  const numColumns = table.table[0].length;
  const shiftedCellUpdates = shiftCellIndexesForColumnMoves(cellUpdates, moveColsOp.cols, numColumns);

  // Replace the move-cols op with a toggle-single-input op
  const toggleOp: ToggleSingleInputTableOp = {
    type: 'toggle-single-input',
    cols: moveColsOp.cols,
    updatedCells: shiftedCellUpdates,
  };
  reorderResult.tableOp = {[table.name]: toggleOp};

  return reorderResult;
}

/**
 * Renames the table with current name `targetTableName` to `newTableName`.
 *
 * @param tables           Map from table name to table
 * @param documentName
 * @param targetTableName  The name of the table to rename.
 * @param newTableName     The new name for the table.
 * @return tableOpOutput - The token updaters have not been ran yet, and must be executed by the
 * caller.
 */
export function renameTable(
  tables: FullTableMap,
  documentName: string | null,
  targetTableName: string,
  newTableName: string
): TableOpOutput {
  const existingNames = Object.keys(tables);
  assert(existingNames.includes(targetTableName), `No table with name ${targetTableName}`);
  assert(!existingNames.includes(newTableName), `Existing table with name ${newTableName}`);

  const tokenUpdater = createRenameTableTokenUpdater(documentName, targetTableName, newTableName);
  const newTable = reactUpdate(tables[targetTableName], {name: {$set: newTableName}});

  const tableOp: TableOpDiffMap = {[newTableName]: {type: 'rename-table', oldName: targetTableName}};

  return {
    table: newTable,
    tokenUpdaters: [tokenUpdater],
    dim: null,
    box: null,
    tableOp,
  };
}

export function _makeCell(text: string): Cell {
  return {text};
}

/**
 * Merges region defined by `selection` in the table `targetTableName`.
 *
 * @param table
 * @param selection        Bounds of region to be merged.
 * @return tableOpOutput - The token updaters have not been ran yet, and must be executed by the
 * caller.
 */
export function mergeRegion(table: Table, selection: MultiSelectionBox): TableOpOutput {
  const {minI, minJ, maxI, maxJ} = selection;
  if (minI === maxI && minJ === maxJ) {
    return {
      table,
      tokenUpdaters: [],
      dim: null,
      box: selection,
    };
  }

  const newTableCells = TableUtil.mergeTableRegion(table, selection).table;
  const newTable = reactUpdate(table, {table: {$set: newTableCells}});

  const cellUpdates: Array<CellIndex> = [];
  for (let row = selection.minI; row <= selection.maxI; row++) {
    for (let column = selection.minJ; column <= selection.maxJ; column++) {
      cellUpdates.push({row, column});
    }
  }

  return {
    table: newTable,
    tokenUpdaters: [],
    dim: null,
    box: {
      minI,
      maxI: minI,
      minJ,
      maxJ: minJ,
    },
    ...(cellUpdates.length > 0 ? {tableOp: {[table.name]: {type: 'update-cells', cells: cellUpdates}}} : {}),
  };
}

/**
 * Unmerges all cells in the region defined by `selection` in the table `targetTableName`.
 *
 * @param table
 * @param computedTable
 * @param selection        Bounds of region to unmerge all cells inside.
 * @return tableOpOutput - The token updaters have not been ran yet, and must be executed by the
 * caller.
 */
export function unmergeRegion(
  table: Table,
  computedTable: EvaluatedTable,
  selection: MultiSelectionBox
): TableOpOutput {
  const {minI, maxI, minJ, maxJ} = selection;
  const tableUpdates: Spec<Cells> = {};
  const cellUpdates: Array<CellIndex> = [];
  for (let i = minI, end = maxI + 1, asc = minI <= end; asc ? i < end : i > end; asc ? i++ : i--) {
    for (let j = minJ, end1 = maxJ + 1, asc1 = minJ <= end1; asc1 ? j < end1 : j > end1; asc1 ? j++ : j--) {
      if (TableUtil.isRenderedMergedCell(i, j, computedTable[i][j])) {
        tableUpdates[i] ??= {};
        // @ts-expect-error
        tableUpdates[i][j] = {
          $apply(cell: Cell): Cell {
            return omit(cell, 'mergeRowSpan', 'mergeColSpan');
          },
        };

        const sourceCell: Cell = table.table[i][j];
        const mergeCell = {
          ...omit(
            pick(sourceCell, ...CELL_FORMAT_PROPERTIES, 'text', 'typedValue'),
            'mergeRowSpan',
            'mergeColSpan'
          ),
        };
        for (let s = i; s < i + sourceCell.mergeRowSpan!; s++) {
          for (let t = j; t < j + sourceCell.mergeColSpan!; t++) {
            cellUpdates.push({row: s, column: t});
            if (s === i && t === j) {
              continue;
            }
            tableUpdates[s] ??= {};
            // @ts-expect-error
            tableUpdates[s][t] = {
              $apply(cell: Cell): Cell {
                return {
                  ...cell,
                  ...mergeCell,
                };
              },
            };
          }
        }
      }
    }
  }

  const newTable = reactUpdate(table, {table: tableUpdates});

  return {
    table: newTable,
    tokenUpdaters: [],
    dim: null,
    box: {minI, maxI, minJ, maxJ},
    ...(cellUpdates.length > 0 ? {tableOp: {[table.name]: {type: 'update-cells', cells: cellUpdates}}} : {}),
  };
}

/**
 * Merges ShiftFormulasTableOpDiffMaps, deduplicating shifted cells values as needed.
 *
 * It is the caller's responsibility to ensure the the inputs do not themselves contain duplicates.
 */
export function mergeShiftFormulaTableOpDiffs(
  a: ShiftFormulasTableOpDiffMap,
  b?: ShiftFormulasTableOpDiffMap
): ShiftFormulasTableOpDiffMap {
  if (b == null) {
    return a;
  }

  return produce(a, (draft) => {
    for (const tableName of Object.keys(b)) {
      if (b[tableName].shiftedCells.length === 0) {
        continue;
      }
      if (!draft.hasOwnProperty(tableName)) {
        draft[tableName] = {type: 'shift-formulas', shiftedCells: b[tableName].shiftedCells};
      } else {
        draft[tableName].shiftedCells = deduplicateCells(
          draft[tableName].shiftedCells,
          b[tableName].shiftedCells
        );
      }
    }
  });
}

function deduplicateCells(a: Array<CellIndex>, b: Array<CellIndex>): Array<CellIndex> {
  const seen = new Set<string>();
  const result: Array<CellIndex> = [];
  a.forEach((cell) => {
    const key = `${cell.row},${cell.column}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push(cell);
    }
  });
  b.forEach((cell) => {
    const key = `${cell.row},${cell.column}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push(cell);
    }
  });
  return result;
}

/** Merges one TableOpDiffMap with shifted formulas across multiple documents. */
export function multidocTableOpFromOpAndShiftedFormulas(
  targetDocumentId: string,
  tableOp: TableOpDiffMap,
  formulaShiftOps?: MultidocShiftFormulasTableOpDiffMap
): MultidocTableOpDiffMap {
  const result = {[targetDocumentId]: tableOp};
  if (formulaShiftOps == null) {
    return result;
  }

  Object.keys(formulaShiftOps).forEach((docId) => {
    if (docId === targetDocumentId) {
      result[docId] = mergeFormulaShiftOpsIntoTableOpDiffMap(tableOp, formulaShiftOps[docId]);
    } else {
      result[docId] = formulaShiftOps[docId];
    }
  });

  return result;
}

/**
 * Merges shifted formulas into a table op diff from a single document.
 * It is the responsibility of the caller to ensure that the original operation allows formula shifting.
 */
function mergeFormulaShiftOpsIntoTableOpDiffMap(
  tableOp: TableOpDiffMap,
  formulaShifts?: ShiftFormulasTableOpDiffMap
): TableOpDiffMap {
  if (formulaShifts == null) {
    return tableOp;
  }

  return produce(tableOp, (draft) => {
    Object.keys(formulaShifts).forEach((tableName) => {
      if (draft[tableName] != null) {
        (draft[tableName] as BaseTableOpDiffWithOptionalFormulaShifts).shiftedCells = deduplicateCells(
          (draft[tableName] as BaseTableOpDiffWithOptionalFormulaShifts).shiftedCells ?? [],
          formulaShifts[tableName].shiftedCells
        );
      } else {
        draft[tableName] = formulaShifts[tableName];
      }
    });
  });
}
